/**
 * The socket, and the small store React reads.
 *
 * Two consumers with very different appetites share this file:
 *
 *   · React — the door, the bar, the cards. Re-renders on a snapshot change,
 *     which happens once per human action.
 *   · The canvas — reads `world` (hunt/world.ts) directly, every frame.
 *
 * So a `kill` message updates the world (no React work at all) and bumps a
 * counter in the snapshot only when something React actually shows has changed.
 * Routing sixty-frames-a-second data through `useSyncExternalStore` is how you
 * get a game that drops frames while nothing is happening.
 */

import type {
  BoardView,
  SaloonSummary,
  SaloonView,
  ServerMsg,
  WaveSummary,
} from '@shared/protocol.js';
import { PROTOCOL_VERSION } from '@shared/protocol.js';
import { clock } from './clock.js';
import { world } from '../hunt/world.js';
import { audio } from '../hunt/audio.js';

export type Status = 'connecting' | 'open' | 'closed';

export interface Snapshot {
  status: Status;
  rtt: number;
  seatId: string | null;
  handle: string;
  view: SaloonView | null;
  saloons: SaloonSummary[];
  board: BoardView | null;
  error: string | null;
  /** Bumped when a wave begins, so the hunt screen can re-mount its cards. */
  waveNonce: number;
  /** Bumped whenever your own shell ledger changes. */
  shellNonce: number;
  summary: WaveSummary | null;
  summaryNextAt: number;
  /** Bumped on any kill/doe, so the scoreboard chips can re-render. */
  scoreNonce: number;
  /** The server told us it is going away. */
  restarting: boolean;
}

const STORE_KEY = 'saloon.profile.v1';

interface Profile {
  handle: string;
  code: string | null;
  token: string | null;
}

function loadProfile(): Profile {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Profile;
      return {
        handle: typeof p.handle === 'string' ? p.handle.slice(0, 14) : '',
        code: typeof p.code === 'string' ? p.code : null,
        token: typeof p.token === 'string' ? p.token : null,
      };
    }
  } catch {
    /* private mode, or a corrupt entry. An empty profile is correct. */
  }
  return { handle: '', code: null, token: null };
}

function saveProfile(p: Profile) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(p));
  } catch {
    /* Safari private mode throws on write. Not fatal; the session still works. */
  }
}

let profile = loadProfile();

let snapshot: Snapshot = {
  status: 'connecting',
  rtt: 0,
  seatId: null,
  handle: profile.handle,
  view: null,
  saloons: [],
  board: null,
  error: null,
  waveNonce: 0,
  shellNonce: 0,
  summary: null,
  summaryNextAt: 0,
  scoreNonce: 0,
  restarting: false,
};

const listeners = new Set<() => void>();

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getSnapshot(): Snapshot {
  return snapshot;
}

function set(patch: Partial<Snapshot>) {
  snapshot = { ...snapshot, ...patch };
  for (const cb of listeners) cb();
}

// ── the socket ──────────────────────────────────────────────────────────────

let sock: WebSocket | null = null;
let pingTimer: number | null = null;
let retryAt = 0;
let attempt = 0;
let wantRejoin = false;

function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws`;
}

export function connect() {
  if (sock && (sock.readyState === WebSocket.OPEN || sock.readyState === WebSocket.CONNECTING)) return;
  set({ status: 'connecting', error: null });

  let s: WebSocket;
  try {
    s = new WebSocket(wsUrl());
  } catch {
    scheduleRetry();
    return;
  }
  sock = s;

  s.onopen = () => {
    attempt = 0;
    set({ status: 'open', error: null, restarting: false });
    startPinging();
    // A reconnect inside a live hunt: the token buys the same seat back, with
    // the same score. This is what makes wifi→LTE a one-second blip instead of
    // the end of somebody's round.
    if (wantRejoin && profile.code && profile.token) {
      send({ t: 'join', code: profile.code, handle: profile.handle, token: profile.token });
    } else {
      send({ t: 'list' });
      send({ t: 'board' });
    }
  };

  s.onmessage = (ev) => {
    let msg: ServerMsg;
    try {
      msg = JSON.parse(String(ev.data)) as ServerMsg;
    } catch {
      return;
    }
    handle(msg);
  };

  s.onerror = () => {
    /* `onclose` always follows; retry logic lives there. */
  };

  s.onclose = () => {
    stopPinging();
    sock = null;
    set({ status: 'closed' });
    scheduleRetry();
  };
}

function scheduleRetry() {
  attempt += 1;
  // Exponential with a ceiling and jitter. The jitter matters: without it every
  // phone in a saloon that just watched the server restart reconnects in the
  // same 50 ms, which is the one moment the server can least afford it.
  const base = Math.min(8000, 400 * Math.pow(1.8, Math.min(attempt, 6)));
  const delay = base * (0.7 + Math.random() * 0.6);
  retryAt = Date.now() + delay;
  window.setTimeout(() => {
    if (Date.now() >= retryAt - 50) connect();
  }, delay);
}

function startPinging() {
  stopPinging();
  const beat = () => {
    if (!sock || sock.readyState !== WebSocket.OPEN) return;
    const c = Date.now();
    send({ t: 'ping', c });
  };
  beat();
  // Fast at first to converge the clock before the player reaches a wave, then
  // slow, because the offset does not move and each ping is a wakeup on a
  // battery-powered radio.
  let sent = 0;
  pingTimer = window.setInterval(() => {
    sent += 1;
    if (sent < 6 || sent % 10 === 0) beat();
  }, 1000);
}

function stopPinging() {
  if (pingTimer !== null) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

function send(msg: unknown) {
  if (!sock || sock.readyState !== WebSocket.OPEN) return;
  try {
    sock.send(JSON.stringify(msg));
  } catch {
    /* the close handler will take it from here */
  }
}

// ── inbound ─────────────────────────────────────────────────────────────────

function seatInfo(seatId: string): { handle: string; hue: number } {
  const seat = snapshot.view?.seats.find((s) => s.id === seatId);
  return { handle: seat?.handle ?? 'someone', hue: seat?.hue ?? 0 };
}

function handle(msg: ServerMsg) {
  switch (msg.t) {
    case 'hello':
      if (msg.version !== PROTOCOL_VERSION) {
        // The page is older than the server it is talking to, which after a
        // deploy is exactly what a phone that never closed its tab looks like.
        set({ error: 'This page is out of date. Pull down to reload.' });
      }
      clock.seed(msg.serverTime);
      return;

    case 'pong':
      clock.sample(msg.c, msg.s, Date.now());
      set({ rtt: clock.rtt });
      return;

    case 'saloons':
      set({ saloons: msg.list });
      return;

    case 'board':
      set({ board: msg.board });
      return;

    case 'joined':
      profile = { handle: profile.handle, code: msg.code, token: msg.token };
      saveProfile(profile);
      wantRejoin = true;
      set({ seatId: msg.seatId, view: msg.view, error: null, summary: null });
      return;

    case 'view': {
      const prevPhase = snapshot.view?.phase;
      set({ view: msg.view });
      if (msg.view.phase !== 'wave' && prevPhase === 'wave') world.active = false;
      if (msg.view.phase === 'bar' || msg.view.phase === 'over') set({ summary: snapshot.summary });
      return;
    }

    case 'wave': {
      world.beginWave(msg);
      // The one announcement in the game. It lands on the site card, three
      // seconds before anything moves, which is the whole idea.
      if (msg.kind === 'legend') audio.play('legend');
      set({ waveNonce: snapshot.waveNonce + 1, summary: null });
      return;
    }

    case 'shells': {
      const wasReloading = world.reloadUntil > 0;
      world.shells = msg.n;
      world.maxShells = msg.max;
      world.reloadUntil = msg.reloadUntil;
      if (msg.reloadUntil) audio.play('rack');
      // The breech closing. Without it the gun comes back silently and the only
      // signal that you can shoot again is a row of pips at the bottom of the
      // screen, which is exactly where nobody is looking during a wave.
      else if (wasReloading) audio.play('reloaded');
      set({ shellNonce: snapshot.shellNonce + 1 });
      return;
    }

    case 'shot': {
      const { hue } = seatInfo(msg.seatId);
      const mine = msg.seatId === snapshot.seatId;
      world.onShot(msg.ax, msg.ay, hue, msg.hit, mine);
      if (mine) audio.play('shot');
      return;
    }

    case 'kill': {
      const { handle: who, hue } = seatInfo(msg.seatId);
      const mine = msg.seatId === snapshot.seatId;
      world.onKill(msg.deerId, who, hue, msg.zone, msg.points, msg.rack, msg.st, mine);
      if (mine) {
        audio.play(msg.rack >= 12 ? 'trophy' : 'hit');
        if (msg.rack >= 12) audio.play('cheer');
        // Additive only: iOS Safari does not implement the Vibration API at
        // all, so nothing about the feel of the gun is built on it (A-17).
        navigator.vibrate?.(msg.rack >= 12 ? [30, 40, 60] : 25);
      } else {
        audio.play('bugle', hue);
      }
      set({ scoreNonce: snapshot.scoreNonce + 1 });
      return;
    }

    case 'doe': {
      const { handle: who, hue } = seatInfo(msg.seatId);
      const mine = msg.seatId === snapshot.seatId;
      world.onDoe(msg.deerId, who, hue, msg.penalty, mine);
      if (mine) world.benched = true;
      audio.play('doe');
      set({ scoreNonce: snapshot.scoreNonce + 1 });
      return;
    }

    case 'varmint': {
      const { handle: who, hue } = seatInfo(msg.seatId);
      const mine = msg.seatId === snapshot.seatId;
      world.onVarmint(msg.varmintId, msg.kind, who, hue, msg.points, mine);
      if (mine) audio.play(msg.kind === 'skunk' ? 'skunk' : 'critter', hue);
      set({ scoreNonce: snapshot.scoreNonce + 1 });
      return;
    }

    case 'escaped':
      world.onEscaped(msg.deerId);
      return;

    case 'contested':
      // Not a miss and not a streak reset. Say so, so it does not read as one.
      world.note(`${msg.byHandle} beat you to it`, 'miss');
      return;

    case 'dryfire':
      audio.play('dryfire');
      if (msg.reason === 'benched') world.note('You are out for this wave.', 'doe');
      else if (msg.reason === 'empty') world.note('Empty.', 'miss');
      return;

    case 'summary':
      audio.play('waveend');
      set({ summary: msg.summary, summaryNextAt: msg.nextAt });
      return;

    case 'error':
      if (msg.code === 'SERVER_RESTARTING') {
        set({ restarting: true, error: msg.message });
        return;
      }
      set({ error: msg.message });
      // A join that failed means the stored code is stale — a saloon that was
      // swept while the phone was in a pocket. Stop trying to get back into it.
      if (msg.code === 'SALOON_NOT_FOUND' || msg.code === 'SALOON_FULL') {
        wantRejoin = false;
        profile = { ...profile, code: null, token: null };
        saveProfile(profile);
        set({ view: null, seatId: null });
        send({ t: 'list' });
      }
      return;
  }
}

// ── commands ────────────────────────────────────────────────────────────────

export function setHandle(handle: string) {
  profile = { ...profile, handle };
  saveProfile(profile);
  set({ handle });
}

export function createSaloon(open: boolean) {
  send({ t: 'create', handle: profile.handle || 'Stranger', open });
}

export function joinSaloon(code: string) {
  send({ t: 'join', code, handle: profile.handle || 'Stranger' });
}

export function leaveSaloon() {
  send({ t: 'leave' });
  wantRejoin = false;
  profile = { ...profile, code: null, token: null };
  saveProfile(profile);
  world.reset();
  set({ view: null, seatId: null, summary: null });
}

export function setReady(ready: boolean) {
  send({ t: 'ready', ready });
}

export function startHunt() {
  send({ t: 'start' });
}

export function playAgain() {
  send({ t: 'again' });
}

export function refreshSaloons() {
  send({ t: 'list' });
  send({ t: 'board' });
}

export function reload() {
  send({ t: 'reload' });
}

let shotSeq = 0;

/**
 * Pull the trigger. The client sends WHERE and WHEN, and nothing else — the
 * server decides everything about what that meant. There is deliberately no
 * optimistic hit here: the muzzle flash and the report fire immediately (they
 * are about the gun, and the gun definitely went off), but nothing on the
 * scoreboard moves until the server says so.
 */
export function fire(sx: number, sy: number) {
  if (!sock || sock.readyState !== WebSocket.OPEN) return;
  shotSeq += 1;
  send({ t: 'shoot', ax: sx, ay: sy, st: clock.shotTime(), seq: shotSeq });
}

export function storedProfile(): Profile {
  return { ...profile };
}
